import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  ClientApprovalService,
  inAppOpenQueryParamForService,
  listPathForApprovalService,
} from './client-first-access-invite.util';

/** Query param used by the dashboard to open a board with a brand pre-selected. */
export const BOARD_BRAND_QUERY_PARAM = 'brandId';

/**
 * Apply `?brandId=` from the dashboard (staff / users who can filter by brand).
 * Clients ignore it — their lists are already brand-scoped.
 */
export function applyBoardBrandFilterFromQuery(
  route: ActivatedRoute,
  opts: {
    isClient: boolean;
    setBrandId: (id: number) => void;
  }
): number | null {
  if (opts.isClient) return null;
  const id = readPositiveIntQuery(route, BOARD_BRAND_QUERY_PARAM);
  if (id == null) return null;
  opts.setBrandId(id);
  return id;
}

/** Reads a positive integer from the current route snapshot query string. */
export function readPositiveIntQuery(route: ActivatedRoute, paramName: string): number | null {
  return readPositiveIntFromParamMap(route.snapshot.queryParamMap, paramName);
}

export function readPositiveIntFromParamMap(params: ParamMap, paramName: string): number | null {
  const raw = params.get(paramName)?.trim();
  return parsePositiveIntId(raw);
}

/** Tolerates trailing junk from email clients (`123"`, `123)`, etc.). */
export function parsePositiveIntId(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const m = s.match(/^(\d+)/);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Fallback when ActivatedRoute query map is empty but the browser URL still has the param. */
export function readPositiveIntFromRouterUrl(router: Router, paramName: string): number | null {
  try {
    const tree = router.parseUrl(router.url || '');
    return parsePositiveIntId(tree.queryParams?.[paramName] ?? null);
  } catch {
    return null;
  }
}

/** Removes a single open-* query param after consuming it (avoids reopen on refresh). */
export function stripOpenQueryParam(router: Router, route: ActivatedRoute, paramName: string): void {
  void router.navigate([], {
    relativeTo: route,
    queryParams: { [paramName]: null },
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });
}

/**
 * Listens for `?openLabdip=` / `?openProto=` etc. on the current board route.
 * Used by email deep-links and the global notifications bell.
 *
 * Opens the detail on the next macrotask (after the list view is up), then strips
 * the query param — avoids a race where stripping navigates before MatDialog opens.
 */
export function bindInAppOpenDeepLink(
  destroyRef: DestroyRef,
  route: ActivatedRoute,
  router: Router,
  paramName: string,
  openDetail: (id: number) => void
): void {
  let handledId: number | null = null;

  const consume = (id: number) => {
    if (handledId === id) return;
    handledId = id;
    // Open after a short delay so the board view / MatDialog overlay exist (cold email links).
    setTimeout(() => {
      try {
        openDetail(id);
      } catch (e) {
        console.error(`[deep-link] openDetail failed for ${paramName}=${id}`, e);
        handledId = null;
        return;
      }
      stripOpenQueryParam(router, route, paramName);
    }, 50);
  };

  route.queryParamMap.pipe(takeUntilDestroyed(destroyRef)).subscribe((params) => {
    const id =
      readPositiveIntFromParamMap(params, paramName) ??
      readPositiveIntFromRouterUrl(router, paramName);
    if (id == null) return;
    consume(id);
  });
}

/** Navigate to the service board and open the detail dialog (staff or client flow on the board). */
export function navigateToInAppServiceOpen(
  router: Router,
  service: ClientApprovalService,
  entityId: number
): Promise<boolean> {
  const id = Number(entityId);
  if (!Number.isFinite(id) || id <= 0) return Promise.resolve(false);

  const path = listPathForApprovalService(service);
  const queryParam = inAppOpenQueryParamForService(service);
  return router.navigate([path], {
    queryParams: { [queryParam]: id },
    queryParamsHandling: 'merge',
  });
}

export function notificationServiceTypeToApprovalService(serviceType: string): ClientApprovalService | null {
  if (serviceType === 'print-embroidery') return 'print_embroidery';
  switch (serviceType) {
    case 'proto':
    case 'labdip':
    case 'bulk':
    case 'sourcing':
    case 'trim':
    case 'print_embroidery':
      return serviceType;
    default:
      return null;
  }
}
